import { Button } from '@rockalingua/ui';
import BaseCard from '@/v2/components/BaseCard/BaseCard';
import usePaymentFlowAnalytics from '@/v2/domains/Analytics/hooks/usePaymentFlowAnalytics.hook';
import {
  PaymentFlowStep,
  PaymentSection,
} from '@/v2/domains/Analytics/types/payment';
import { subscriptionTypes } from '@rockalingua/shared/subscriptions/constants';
import { useExpiredSubscriptionModalStore } from '@/v2/domains/Subscriptions/stores/useExpiredSubscriptionModal.store';
import useManageSubscriptionActions from '@/v2/domains/Subscriptions/hooks/useManageSubscriptionActions.hook';
import useGetSubscriptionTypesQuery from '@/v2/domains/Subscriptions/queries/useGetSubscriptionTypesQuery.hook';
import type { SubscriptionType } from '@/v2/domains/Subscriptions/types/subscription';
import ExpiredSubscriptionContainer from '../ExpiredSubscriptionBannerContainer/ExpiredSubscriptionContainer';

import {
  PlansSection,
  PlanContainer,
  PlanHeader,
  PlanName,
  PlanTitle,
  PriceRow,
  PriceAmount,
  PriceLabel,
  PlanSubtitle,
  ButtonGroup,
  ButtonGroupContainer,
  FeaturesSection,
  FeaturesTitle,
  FeatureItem,
  Footer,
} from './TeacherProExpiredSubscriptionBanner.styled';
import { useTrialOrPaidText } from '../../hooks/useTrialOrPaidText.hook';

type TeacherProExpiredSubscriptionBannerProps = {
  title?: string;
  size?: 'large' | 'small';
};

export default function TeacherProExpiredSubscriptionBanner({
  title,
  size = 'large',
}: TeacherProExpiredSubscriptionBannerProps) {
  const { onContinue, onOptionSelected, onSkip } = usePaymentFlowAnalytics({
    section: PaymentSection.paywall,
    step: PaymentFlowStep.payClicked,
  });
  const { subscribe } = useManageSubscriptionActions();
  const { setModalClosed } = useExpiredSubscriptionModalStore();

  const { data: dynamicSubscriptionTypes } = useGetSubscriptionTypesQuery();

  const { teacherPro, teacher } = getBannerSubscriptionTypes(
    dynamicSubscriptionTypes as SubscriptionType[]
  );

  const defaultTitle = useTrialOrPaidText({
    trialText:
      'Your free trial has ended, keep your classes & students on track',
    paidText:
      'Your subscription has expired, keep your classes & students on track',
  });

  const handleContinue = (subscriptionType: SubscriptionType) => {
    onOptionSelected({ option: subscriptionType.code });
    setModalClosed(true);

    subscribe();
  };

  return (
    <ExpiredSubscriptionContainer
      title={title || defaultTitle}
      subtitle="Keep your students engaged with Spanish through songs, videos, and interactive activities. Choose the plan that fits your classroom best."
      size={size}
    >
      <PlansSection>
        <BaseCard clickable={false}>
          <PlanContainer>
            <PlanHeader>
              <PlanName>
                <PlanTitle>{teacherPro.name}</PlanTitle>
                <PlanSubtitle>Access to a pro subscription</PlanSubtitle>
              </PlanName>
              <PriceRow>
                <PriceAmount>${teacherPro.monthPrice}</PriceAmount>
                <PriceLabel>Per month</PriceLabel>
              </PriceRow>
            </PlanHeader>

            <ButtonGroupContainer>
              <ButtonGroup>
                <Button
                  type="primary"
                  size={size}
                  block
                  onClick={() => handleContinue(teacherPro)}
                >
                  Continue with {teacherPro.name}
                </Button>
                <Button
                  type="link"
                  href="mailto:info@rockalingua.com"
                  size={size}
                  onClick={() => {
                    onContinue();
                  }}
                >
                  School purchase? Talk to sales.
                </Button>
              </ButtonGroup>
            </ButtonGroupContainer>

            <FeaturesSection>
              <FeaturesTitle>All started benefits and:</FeaturesTitle>
              <FeatureItem>
                Sync with Google Classroom, Clever, and ClassLink
              </FeatureItem>
              <FeatureItem>
                Create and manage sign tasks to students
              </FeatureItem>
              <FeatureItem>Track student progress</FeatureItem>
            </FeaturesSection>
          </PlanContainer>
        </BaseCard>

        {size == 'large' && (
          <BaseCard clickable={false}>
            <PlanContainer>
              <PlanHeader>
                <PlanName>
                  <PlanTitle>{teacher.name}</PlanTitle>
                  <PlanSubtitle>The most basic subscription</PlanSubtitle>
                </PlanName>
                <PriceRow>
                  <PriceAmount>${teacher.monthPrice}</PriceAmount>
                  <PriceLabel>Per month</PriceLabel>
                </PriceRow>
              </PlanHeader>

              <ButtonGroup>
                <Button
                  size={size}
                  block
                  onClick={() => handleContinue(teacher)}
                >
                  Continue with {teacher.name}
                </Button>
                <Button
                  type="link"
                  href="mailto:info@rockalingua.com"
                  size={size}
                  onClick={() => {
                    onContinue();
                  }}
                >
                  School purchase? Talk to sales.
                </Button>
              </ButtonGroup>

              <FeaturesSection>
                <FeaturesTitle>Started benefits</FeaturesTitle>
                <FeatureItem>
                  Full access to all Rockalingua content (Videos, songs...)
                </FeatureItem>
                <FeatureItem>No class or student management</FeatureItem>
                <FeatureItem>No track student progress</FeatureItem>
              </FeaturesSection>
            </PlanContainer>
          </BaseCard>
        )}
      </PlansSection>

      {size == 'large' && (
        <Footer>
          <Button
            type="link"
            onClick={() => {
              onSkip();
              setModalClosed(true);
            }}
            size="large"
          >
            Leave for now
          </Button>
        </Footer>
      )}
    </ExpiredSubscriptionContainer>
  );
}

function getBannerSubscriptionTypes(
  dynamicSubscriptionTypes: SubscriptionType[] = []
): {
  teacherPro: SubscriptionType;
  teacher: SubscriptionType;
} {
  const teacherPro =
    dynamicSubscriptionTypes.find(
      (subscriptionType): subscriptionType is SubscriptionType =>
        subscriptionType?.code == subscriptionTypes.teacherPro.code
    ) ?? subscriptionTypes.teacherPro;

  const teacher =
    dynamicSubscriptionTypes.find(
      (subscriptionType): subscriptionType is SubscriptionType =>
        subscriptionType?.code == subscriptionTypes.teacher.code
    ) ?? subscriptionTypes.teacher;

  return {
    teacherPro,
    teacher,
  };
}
